/**
 * Where an arrow key moves the selection inside a radio group, or `null` for a key that means
 * nothing there (or a current option the list does not hold).
 *
 * A `role="radio"` attribute buys none of this: the WAI-ARIA radio-group pattern is arrow-key
 * movement plus a roving tab stop, and both have to be written. This is the movement half, kept out
 * of React because "which option is next" is a rule and not a rendering concern. The group WRAPS, as
 * the pattern prescribes for a group of any size.
 *
 * Shared by every group built from `OfferOption` rows: the exit offer's terms and the first-month
 * dialog's three tiers.
 */
export function radioOptionForArrowKey<T>(key: string, options: readonly T[], current: T | null): T | null {
	const step = key === "ArrowRight" || key === "ArrowDown"
		? 1
		: key === "ArrowLeft" || key === "ArrowUp" ? -1 : 0;
	// With nothing checked the arrows move from where the reader's focus is, the group's tab stop. A current
	// option the list does not hold still answers nothing, as it always did.
	const from = current === null ? radioTabStop(options, null) : current;
	const index = from === null ? -1 : options.indexOf(from);
	if (step === 0 || index === -1) {
		return null;
	}
	const total = options.length;
	return options[(index + step + total) % total];
}

/**
 * Which option holds the group's ONE tab stop: the checked option, or the first while none is checked
 * (kitecorp/transferify-ui#3229).
 *
 * The roving tab stop is the other half of the pattern `role="radio"` does not provide. Keyed on the checked
 * option alone, a group that starts with nothing checked (the over-limit pay dialog's two rows, where neither
 * may be pre-selected) gave every row tabindex -1, so the group could not be reached with Tab at all. The
 * WAI-ARIA pattern gives the first option the stop in that case.
 */
export function radioTabStop<T>(options: readonly T[], current: T | null): T | null {
	if (current !== null && options.includes(current)) {
		return current;
	}
	return options.length === 0 ? null : options[0];
}
